import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { Box, Center, Column, ConfirmPopup, Row } from '~/commonComponents';
import { JobPreviewCard } from '~/commonComponents/JobPreviewCard';
import { useAppSelector } from '~/hooks';
import WarningIcon from '~/images/icons/Warning';
import { Colors } from '~/themes/colors';
import { AddMoreOnsiteInterviewButton } from '../AddMoreOnsiteInterviewButton';
import { useDeleteOnsiteInterview } from './useDeleteOnsiteInterview';
import { fireInterviewRequestAddInterviewsClicked } from '~/posthogs/events';

interface IOnsiteInterviewPreviewerProps {
  jobPostingId: string;
}

export const OnsiteInterviewPreviewer = ({ jobPostingId }: IOnsiteInterviewPreviewerProps) => {
  const navigate = useNavigate();

  const interviews = useAppSelector((state) => {
    if (!jobPostingId) return [];
    return state.permanentJobs.onsiteInterviews[jobPostingId] ?? [];
  });

  const fmtOrderedInterviews = useMemo(
    () => [...interviews].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()),
    [interviews],
  );

  const {
    isWarningOpen,
    onCloseWarning,
    onDelete: handleDeleteClick,
  } = useDeleteOnsiteInterview({ jobPostingId });

  const handleEditClick = (id: string) =>
    navigate(`/permanent-jobs/postings/${jobPostingId}/onsite-interviews/${id}/edit`);

  if (!jobPostingId) return null;

  return (
    <>
      <Column marginInline="auto" width={892}>
        <Row alignItems="center" justifyContent="flex-end" paddingInline={40}>
          <AddMoreOnsiteInterviewButton
            includeLocationState
            jobPostingId={jobPostingId}
            variant="lite"
            onTrack={() => {
              fireInterviewRequestAddInterviewsClicked({ postingId: jobPostingId });
            }}
          />
        </Row>
        <Box
          columnGap={20}
          display="grid"
          gridTemplateColumns="repeat(2, 1fr)"
          marginTop={24}
          rowGap={16}
        >
          {fmtOrderedInterviews.map((it) => (
            <JobPreviewCard
              breakTime={it.breakTime || ''}
              date={new Date(it.date)}
              endsAt={it.hoursDeparture}
              hourlyRate={it.hourlyRate}
              isAssistedHygiene={it.isAssistedHygiene}
              isBreakTimePaid={it.isPaidBreakTime}
              isHideTips
              key={it.id}
              profession={it.profession}
              subProfession={it.subProfession}
              specialty={it.specialty}
              startsAt={it.hoursArrival}
              testName="onsite-interviews.jobs"
              onDelete={() => handleDeleteClick(it.id)}
              onEdit={() => handleEditClick(it.id)}
            />
          ))}
        </Box>
      </Column>

      {isWarningOpen && (
        // @ts-expect-error - ConfirmPopup still written in JavaScript
        <ConfirmPopup
          Icon={() => (
            <Center marginBottom={20}>
              <WarningIcon width={80} height={80} color={Colors.warning_500} />
            </Center>
          )}
          title="Onsite Interview Needed to Post Job"
          description="You must keep at least one onsite interview to post this job. Adding more interviews gives you even better chances to fill the position successfully."
          rightButtonText="Close"
          rightButtonAction={onCloseWarning}
        />
      )}
    </>
  );
};
